import { formatDistanceToNowStrict } from 'date-fns';

import type { InquiryV2 } from '@/services/getInquiriesV2';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import {
  getAnonymousPrimumRoutedTitleExpert,
  isAskAnonymouslyAuthoredInquiry,
} from '@/util/inquiryDisplay';
import { getGroupTabLabel, getInquiryParentKey } from '@/util/inquiryGroups';
import { isAskAnonymously } from '@/util/userFilters';

/**
 * An inquiry group is an original inquiry together with the expert relay threads sent out from it.
 *
 * Grouping runs on the client over the rows a tab has already loaded, so it can only attach what
 * it can see. A relay whose original is not loaded is left in `remaining` as an ordinary row, so a
 * thread is never hidden. An original whose relays sit on a later page shows only the loaded ones,
 * and an inquiry group can split across pages until grouping moves to the server.
 */
export type QuestionGroup = {
  /**
   * `question-group-` and the original's conversation id, so the server's group and the client's own
   * for the same question share one key and keep their expanded state when the list switches between
   * them. The server's entry key stands in only for a group with no original at all.
   */
  key: string;
  original: InquiryV2;
  /**
   * True when the original was fetched on its own rather than found in the loaded rows. It has no
   * place in the API's order, so it never decides where the inquiry group sits in the list.
   */
  originalFetched?: boolean;
  /**
   * The submitter's own threads for this question: the original and the loaded siblings of the
   * fan-out it came from. The inquiry group consumes all of them, so no sibling is left behind as a flat
   * row. Empty on a relay-side card, whose original only titles it.
   */
  originalGroup: InquiryV2[];
  /**
   * The rows' threads in the order they were sent: the relays, or on a submitter-side card the
   * submitter's own threads, one per pick.
   */
  threads: InquiryV2[];
  /**
   * Which side of the relay the card holds, when the server grouped it. Asked per side, the two
   * sides of one question are two cards, each with its own state: the submitter's threads to Ask
   * Anonymously, and Ask Anonymously's relays to experts. `both` is one card holding the two: the
   * relays are `threads`, the submitter's threads are `originalGroup`, and the card nests the relays
   * under the HCP thread each was sent on from, with `getQuestionGroupExpertEntries`.
   */
  side?: 'submitter' | 'relay' | 'both';
  /**
   * Experts the submitter picked that nobody has relayed to yet. Each stands for the submitter's own
   * fan-out thread that carries the pick, so it opens like any thread. The server sends them in its
   * own order; client grouping finds them from the picks on the loaded fan-out threads.
   */
  notSentExperts?: NotSentExpert[];
  /**
   * For a relay of a `both` card, the conversation id of the submitter thread it was sent on from.
   * Only relays with a stored link appear, so an older relay has no entry and the card falls back to
   * matching it by the expert the submitter picked.
   */
  relaySources?: Readonly<Record<string, string>>;
};

export type NotSentExpert = {
  id: number;
  name: string;
  lastName: string;
  /** The submitter's fan-out thread that carries the pick. */
  thread: InquiryV2;
};

/** The one key format for a group, in server and client grouping alike. */
export function getQuestionGroupKey(identity: string) {
  return `question-group-${identity}`;
}

export type QuestionGroupSplit = {
  questionGroups: QuestionGroup[];
  /** Everything that is not part of an inquiry group, in its incoming order. */
  remaining: InquiryV2[];
};

export type QuestionGroupThreadStatus = 'open' | 'close_requested' | 'closed';

export const ANONYMOUS_SUBMITTER_LABEL = 'Anonymous submitter';

export const TITLE_SEPARATOR = ' · ';

/**
 * A relay is the expert-side thread the Ask Anonymously account forwarded a question into, and it
 * points back at the original through `referenceInquiryIds`. Other inquiries also carry reference
 * ids, such as a follow-up linked to an earlier question, so the author test is what keeps those
 * out of an inquiry group.
 */
export function isRelayThread(inquiry: InquiryV2): boolean {
  return isAskAnonymouslyAuthoredInquiry(inquiry) && (inquiry.referenceInquiryIds?.length ?? 0) > 0;
}

function toTimestamp(value?: string | null) {
  return parseThreadTimestamp(value).getTime();
}

/** Oldest first, by the time each thread was sent, then by id. */
export function compareSentOrder(left: InquiryV2, right: InquiryV2) {
  const sentDiff = toTimestamp(left.createdDate) - toTimestamp(right.createdDate);

  if (sentDiff !== 0) {
    return sentDiff;
  }

  return left.id - right.id;
}

/**
 * The experts the submitter picked on these fan-out threads that nobody has relayed to yet, the
 * client's reading of the server's Not sent rule: a pick counts while the thread carrying it is not
 * closed, names an expert rather than the routing account, and has no relay to that expert among
 * `relays`. One row per expert, in the order the threads were sent.
 */
export function findNotSentExperts(
  fanOutThreads: readonly InquiryV2[],
  relays: readonly InquiryV2[]
): NotSentExpert[] {
  const relayedIds = new Set(relays.map((relay) => relay.recipient?.id));

  const seen = new Set<number>();

  return [...fanOutThreads].sort(compareSentOrder).flatMap((thread): NotSentExpert[] => {
    const expert = getAnonymousPrimumRoutedTitleExpert(thread);

    if (!expert || getThreadStatus(thread) === 'closed') {
      return [];
    }

    const name = getProfileNameAndInitials({
      firstName: expert.firstName,
      lastName: expert.lastName,
      identifiesAsDoctor: expert.identifiesAsDoctor,
    }).name;

    if (isAskAnonymously(name) || relayedIds.has(expert.id) || seen.has(expert.id)) {
      return [];
    }

    seen.add(expert.id);

    return [
      {
        id: expert.id,
        name,
        lastName: expert.lastName?.trim() || expert.firstName?.trim() || '',
        thread,
      },
    ];
  });
}

/**
 * The thread a fan-out set's card is titled from: the root the siblings point at when it is loaded,
 * otherwise the first one sent.
 */
function pickFanOutOriginal(fanOutThreads: readonly InquiryV2[]) {
  return (
    fanOutThreads.find((thread) => thread.parentId === null || thread.parentId === undefined) ??
    [...fanOutThreads].sort(compareSentOrder)[0]
  );
}

/**
 * Splits a tab's loaded inquiries into inquiry groups and everything else.
 *
 * A relay attaches to the first of its referenced ids that resolves to a loaded inquiry that is
 * not itself a relay. A fan-out the submitter sent with experts picked forms an inquiry group even
 * before anyone relays it, with each pick still waiting shown as Not sent, as the server does. An
 * original with no relay and no pick waiting is not an inquiry group, so it stays a flat row.
 */
export function buildQuestionGroups(
  inquiries: readonly InquiryV2[],
  fetchedOriginals: readonly InquiryV2[] = []
): QuestionGroupSplit {
  const byConversationId = new Map(inquiries.map((inquiry) => [inquiry.conversationId, inquiry]));

  const loadedIds = new Set(byConversationId.keys());

  // A fetched original that has since loaded in the page is the loaded one, so it never doubles.
  fetchedOriginals.forEach((original) => {
    if (!byConversationId.has(original.conversationId)) {
      byConversationId.set(original.conversationId, original);
    }
  });

  const threadsByOriginal = new Map<string, InquiryV2[]>();

  inquiries.forEach((inquiry) => {
    if (!isRelayThread(inquiry)) {
      return;
    }

    const originalId = inquiry.referenceInquiryIds.find((referenceId) => {
      const candidate = byConversationId.get(referenceId);

      return candidate !== undefined && !isRelayThread(candidate);
    });

    if (!originalId) {
      return;
    }

    const threads = threadsByOriginal.get(originalId) ?? [];

    threads.push(inquiry);

    threadsByOriginal.set(originalId, threads);
  });

  const consumed = new Set<string>();

  const questionGroups: QuestionGroup[] = [];

  threadsByOriginal.forEach((threads, originalId) => {
    const original = byConversationId.get(originalId);

    if (!original) {
      return;
    }

    const parentKey = getInquiryParentKey(original);

    const siblings = inquiries.filter(
      (inquiry) =>
        inquiry.conversationId !== originalId &&
        !isRelayThread(inquiry) &&
        !consumed.has(inquiry.conversationId) &&
        getInquiryParentKey(inquiry) === parentKey
    );

    consumed.add(originalId);

    siblings.forEach((sibling) => consumed.add(sibling.conversationId));

    threads.forEach((thread) => consumed.add(thread.conversationId));

    const originalGroup = [original, ...siblings];

    const notSentExperts = findNotSentExperts(originalGroup, threads);

    questionGroups.push({
      key: getQuestionGroupKey(originalId),
      original,
      originalFetched: !loadedIds.has(originalId),
      originalGroup,
      threads: [...threads].sort(compareSentOrder),
      ...(notSentExperts.length ? { notSentExperts } : {}),
    });
  });

  const unrelayedFanOuts = new Map<number, InquiryV2[]>();

  inquiries.forEach((inquiry) => {
    if (consumed.has(inquiry.conversationId) || isRelayThread(inquiry)) {
      return;
    }

    const parentKey = getInquiryParentKey(inquiry);

    unrelayedFanOuts.set(parentKey, [...(unrelayedFanOuts.get(parentKey) ?? []), inquiry]);
  });

  unrelayedFanOuts.forEach((fanOutThreads) => {
    const notSentExperts = findNotSentExperts(fanOutThreads, []);

    if (!notSentExperts.length) {
      return;
    }

    const original = pickFanOutOriginal(fanOutThreads);

    fanOutThreads.forEach((thread) => consumed.add(thread.conversationId));

    questionGroups.push({
      key: getQuestionGroupKey(original.conversationId),
      original,
      originalGroup: [original, ...fanOutThreads.filter((thread) => thread !== original)],
      threads: [],
      notSentExperts,
    });
  });

  return {
    questionGroups,
    remaining: inquiries.filter((inquiry) => !consumed.has(inquiry.conversationId)),
  };
}

/**
 * The conversation ids of originals to fetch: for each relay none of whose referenced inquiries is
 * loaded, the first reference. Each id appears once, in the order the relays appear.
 */
export function findMissingOriginalIds(inquiries: readonly InquiryV2[]): string[] {
  const loadedIds = new Set(inquiries.map((inquiry) => inquiry.conversationId));

  const missing = new Set<string>();

  inquiries.forEach((inquiry) => {
    if (!isRelayThread(inquiry)) {
      return;
    }

    if (inquiry.referenceInquiryIds.some((referenceId) => loadedIds.has(referenceId))) {
      return;
    }

    missing.add(inquiry.referenceInquiryIds[0]);
  });

  return [...missing];
}

/**
 * Which of the wanted ids may be requested now. Settled ids never count against the limit, so the
 * list works through a long backlog a few requests at a time instead of all at once.
 */
export function selectOriginalIdsToFetch(
  wantedIds: readonly string[],
  isSettled: (conversationId: string) => boolean,
  maxInFlight: number
): Set<string> {
  const allowed = new Set<string>();

  let unsettled = 0;

  wantedIds.forEach((conversationId) => {
    if (isSettled(conversationId)) {
      allowed.add(conversationId);

      return;
    }

    if (unsettled < maxInFlight) {
      allowed.add(conversationId);

      unsettled += 1;
    }
  });

  return allowed;
}

/** Every conversation an inquiry group holds, for the selected-row test and the chat's sibling tabs. */
export function getQuestionGroupConversationIds(questionGroup: QuestionGroup) {
  // A submitter-side card's rows are its original group, so each id is listed once.
  return [
    ...new Set([
      ...questionGroup.originalGroup.map((inquiry) => inquiry.conversationId),
      ...questionGroup.threads.map((thread) => thread.conversationId),
      ...(questionGroup.notSentExperts ?? []).map((expert) => expert.thread.conversationId),
    ]),
  ];
}

export function getThreadStatus(thread: InquiryV2): QuestionGroupThreadStatus {
  if (thread.inquiryStatus === 'closed') {
    return 'closed';
  }

  if (thread.inquiryStatus === 'inactive') {
    return 'close_requested';
  }

  return 'open';
}

/** True while the expert has not written back. `firstRespondedAt` is set once, on the first reply. */
export function isThreadWaiting(thread: InquiryV2) {
  return getThreadStatus(thread) === 'open' && !thread.firstRespondedAt;
}

/**
 * Postgres text, "2026-10-28 22:42:33+00", rewritten as ISO 8601, "2026-10-28T22:42:33+00:00":
 * the space becomes a T, and an hour-only offset after a time gains its minutes. Anything else,
 * ISO and a bare date included, comes back unchanged.
 */
export function toIsoTimestamp(value: string) {
  return value.replace(
    /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)([+-]\d{2})?$/,
    (_match, day: string, time: string, offset?: string) =>
      `${day}T${time}${offset ? `${offset}:00` : ''}`
  );
}

/**
 * A thread timestamp as a Date, from the first of `values` that parses.
 *
 * The API sends some timestamps as ISO strings and `lastMessageAt` as Postgres text,
 * "2026-10-28 22:42:33+00". A browser parses both, but Hermes on iOS and Android parses only ISO, so
 * the Postgres form came back Invalid Date there and the card's "3 hours ago" threw and took the
 * whole inbox down. The space and the hour-only offset are rewritten into ISO before parsing, and a
 * value that still does not parse is skipped rather than returned.
 */
export function parseThreadTimestamp(...values: (string | null | undefined)[]): Date {
  for (const value of values) {
    if (!value) {
      continue;
    }

    const date = new Date(toIsoTimestamp(value));

    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  }

  return new Date(0);
}

export function getThreadSentAt(thread: InquiryV2) {
  return parseThreadTimestamp(thread.createdDate, thread.lastResponseDate);
}

export function getThreadActivityAt(thread: InquiryV2) {
  return parseThreadTimestamp(thread.lastMessageAt, thread.lastResponseDate, thread.createdDate);
}

/**
 * The expert a thread is for: its recipient, or on a submitter's thread to Ask Anonymously the
 * expert it picked, since the recipient there is the routing account.
 */
function getThreadExpert(thread: InquiryV2) {
  return getAnonymousPrimumRoutedTitleExpert(thread) ?? thread.recipient;
}

export function getThreadExpertName(thread: InquiryV2) {
  const expert = getThreadExpert(thread);

  return getProfileNameAndInitials({
    firstName: expert?.firstName,
    lastName: expert?.lastName,
    identifiesAsDoctor: expert?.identifiesAsDoctor,
  }).name;
}

/**
 * "3 hours ago", in the wording the app's comments use. Empty for an invalid date, since
 * date-fns throws on one and a card's time line is not worth the inbox.
 */
export function formatAgo(date: Date) {
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return formatDistanceToNowStrict(date, { addSuffix: true });
}

function newestBy(threads: readonly InquiryV2[], getDate: (thread: InquiryV2) => Date) {
  return threads.reduce<InquiryV2 | undefined>(
    (newest, thread) =>
      !newest || getDate(thread).getTime() > getDate(newest).getTime() ? thread : newest,
    undefined
  );
}

function oldestBy(threads: readonly InquiryV2[], getDate: (thread: InquiryV2) => Date) {
  return threads.reduce<InquiryV2 | undefined>(
    (oldest, thread) =>
      !oldest || getDate(thread).getTime() < getDate(oldest).getTime() ? thread : oldest,
    undefined
  );
}

/**
 * One row of an open card: an expert's thread, a picked expert's fan-out thread not yet sent, or
 * the submitter's own thread with Ask Anonymously while it holds an unread message.
 */
export type QuestionGroupRow = {
  thread: InquiryV2;
  name: string;
  lastName: string;
  notSent: boolean;
  /**
   * True when the thread is the submitter's with Ask Anonymously rather than one addressed to an
   * expert: every not-sent row, and the submitter's own row.
   */
  fromSubmitter: boolean;
  /**
   * The expert's photo. A sent row's thread is addressed to the expert; a not-sent row's thread is
   * the submitter's, addressed to the routing account, so its photo comes from the pick it carries.
   */
  avatar: NonNullable<InquiryV2['recipient']>['avatar'] | null;
};

function getPersonName(person: InquiryV2['author'] | null | undefined) {
  if (!person) {
    return '';
  }

  return getProfileNameAndInitials({
    firstName: person.firstName,
    lastName: person.lastName,
    identifiesAsDoctor: person.identifiesAsDoctor,
  }).name;
}

function toSubmitterRow(thread: InquiryV2): QuestionGroupRow {
  const submitter = thread.attributedSubmitter ?? thread.author;

  const name = getPersonName(submitter);

  return {
    thread,
    name,
    lastName: submitter?.lastName?.trim() || name,
    notSent: false,
    fromSubmitter: true,
    avatar: submitter?.avatar ?? null,
  };
}

/**
 * The card's rows: the expert threads in order, then the picked experts nobody has been sent to.
 * A card never lists more rows than experts, since a fourth row on a three-expert card reads as a
 * fourth expert.
 */
export function getQuestionGroupRows(
  questionGroup: Pick<QuestionGroup, 'threads' | 'notSentExperts'> &
    Partial<Pick<QuestionGroup, 'side'>>
): QuestionGroupRow[] {
  // A submitter-side card's rows are the submitter's own threads, so they open and draw as theirs.
  const fromSubmitter = questionGroup.side === 'submitter';

  const notSentRows = (questionGroup.notSentExperts ?? []).map((expert) => ({
    thread: expert.thread,
    name: expert.name,
    lastName: expert.lastName,
    notSent: true,
    fromSubmitter: true,
    avatar: getAnonymousPrimumRoutedTitleExpert(expert.thread)?.avatar ?? null,
  }));

  return [
    ...questionGroup.threads.map((thread) => ({
      thread,
      name: getThreadExpertName(thread),
      lastName: getThreadLastName(thread),
      notSent: false,
      fromSubmitter,
      avatar: getThreadExpert(thread)?.avatar ?? null,
    })),
    ...notSentRows,
  ];
}

/**
 * One entry of a `both` card. A top-level entry is one HCP thread, with the relays sent on from it
 * nested beneath. The one other top-level shape is a relay no HCP thread claims, which has no HCP
 * line. A nested entry is an expert the HCP thread was sent on to, other than the one it picked.
 */
export type QuestionGroupExpertEntry = {
  /** The HCP thread's conversation id, or the relayed expert's id for an entry with no HCP thread. */
  key: string;
  /** The expert the thread picked, or the HCP's own name for a thread that picked nobody. */
  name: string;
  /** The HCP's thread names no expert, so the entry is titled for the HCP and says it went to Ask Anonymously. */
  toAskAnonymously: boolean;
  /** The expert's id when there is one, otherwise null. */
  expertId: number | null;
  avatar: QuestionGroupRow['avatar'];
  /** The relay went to an expert the HCP thread did not pick, so only staff added them. */
  addedByStaff: boolean;
  /** The HCP thread the entry stands for. Null for a relay no HCP thread claims, and on a nested entry. */
  hcpRow: QuestionGroupRow | null;
  /**
   * Ask Anonymously's relays to this entry's expert, newest first, so an older relay stays reachable
   * rather than becoming an entry of its own.
   */
  expertRows: QuestionGroupRow[];
  /** A pick nobody has relayed to: the entry still shows an Expert line, saying it is not sent. */
  expertNotSent: boolean;
  /** The other experts the HCP thread was sent on to, one nested entry per expert. */
  children: QuestionGroupExpertEntry[];
};

/** The expert an HCP thread picked, or undefined when it names nobody but Ask Anonymously. */
function getPickedExpertId(thread: InquiryV2) {
  const expert = getAnonymousPrimumRoutedTitleExpert(thread);

  if (!expert) {
    return undefined;
  }

  const name = getProfileNameAndInitials({
    firstName: expert.firstName,
    lastName: expert.lastName,
    identifiesAsDoctor: expert.identifiesAsDoctor,
  }).name;

  return isAskAnonymously(name) ? undefined : expert.id;
}

/**
 * Relays as one nested entry per expert, in the order each expert first appears. An expert's repeat
 * relays join that entry as extra rows, in the order given, so none is lost or listed twice.
 */
function toRelayEntries(relays: readonly InquiryV2[]): QuestionGroupExpertEntry[] {
  const entries = new Map<string, QuestionGroupExpertEntry>();

  relays.forEach((relay) => {
    const expertId = relay.recipient?.id;

    const key = expertId === undefined ? relay.conversationId : `expert-${expertId}`;

    const name = getThreadExpertName(relay);

    const avatar = getThreadExpert(relay)?.avatar ?? null;

    const row: QuestionGroupRow = {
      thread: relay,
      name,
      lastName: getThreadLastName(relay),
      notSent: false,
      fromSubmitter: false,
      avatar,
    };

    const existing = entries.get(key);

    if (existing) {
      existing.expertRows.push(row);

      return;
    }

    entries.set(key, {
      key,
      name,
      toAskAnonymously: false,
      expertId: expertId ?? null,
      avatar,
      addedByStaff: false,
      hcpRow: null,
      expertRows: [row],
      expertNotSent: false,
      children: [],
    });
  });

  return [...entries.values()];
}

/**
 * The entries of a `both` card: one per HCP thread, in the order the threads were sent, each with
 * the relays that were sent on from it nested beneath.
 *
 * A relay nests under the thread its stored link names (`relaySources`). The link is the only thing
 * that can say which thread a relay came from when several threads picked the same expert or none
 * did, so it wins over any guess. The server links only an expert's newest relay, so an older relay
 * to an expert whose newest one is linked nests under that same thread, as an extra line under the
 * same expert, rather than being guessed onto another thread that picked them. A relay with no link
 * and no linked relay to its expert, or one naming a thread this card does not hold, is placed the
 * way it was before links existed: under the HCP thread that picked its expert,
 * else under the earliest thread that picked nobody, else as an entry of its own. That last one is
 * "added by staff" when the group has picks, since nothing explains the expert otherwise.
 *
 * Under a thread, the relays to the expert it picked sit directly beneath its HCP line, and the
 * name is not repeated. Relays to any other expert nest as their own entry with a name, and are
 * "added by staff" only when the thread did pick someone: a thread that picked nobody was sent on to
 * whoever staff chose. A pick that no relay in the group went to yet shows as not sent.
 *
 * `threads` must already hold an expert's newest relay first, as the server sends them.
 */
export function getQuestionGroupExpertEntries(
  questionGroup: Pick<QuestionGroup, 'originalGroup' | 'threads' | 'relaySources'>
): QuestionGroupExpertEntry[] {
  const hcpThreads = [...questionGroup.originalGroup].sort(compareSentOrder).map((thread) => ({
    thread,
    pickedId: getPickedExpertId(thread),
  }));

  const relaySources = questionGroup.relaySources ?? {};

  const hcpIds = new Set(hcpThreads.map((hcp) => hcp.thread.conversationId));

  const relayedExpertIds = new Set(questionGroup.threads.map((relay) => relay.recipient?.id));

  const linkedHostIdByExpertId = new Map<number, string>();

  questionGroup.threads.forEach((relay) => {
    const sourceId = relaySources[relay.conversationId];

    const expertId = relay.recipient?.id;

    if (
      sourceId &&
      hcpIds.has(sourceId) &&
      expertId !== undefined &&
      !linkedHostIdByExpertId.has(expertId)
    ) {
      linkedHostIdByExpertId.set(expertId, sourceId);
    }
  });

  const findHostId = (relay: InquiryV2) => {
    const sourceId = relaySources[relay.conversationId];

    if (sourceId && hcpIds.has(sourceId)) {
      return sourceId;
    }

    const expertId = relay.recipient?.id;

    const linkedHostId = expertId === undefined ? undefined : linkedHostIdByExpertId.get(expertId);

    if (linkedHostId) {
      return linkedHostId;
    }

    return (
      hcpThreads.find((hcp) => expertId !== undefined && hcp.pickedId === expertId) ??
      hcpThreads.find((hcp) => hcp.pickedId === undefined)
    )?.thread.conversationId;
  };

  const relaysByHostId = new Map<string, InquiryV2[]>();

  const unclaimedRelays: InquiryV2[] = [];

  questionGroup.threads.forEach((relay) => {
    const hostId = findHostId(relay);

    if (hostId === undefined) {
      unclaimedRelays.push(relay);

      return;
    }

    relaysByHostId.set(hostId, [...(relaysByHostId.get(hostId) ?? []), relay]);
  });

  const hcpEntries = hcpThreads.map(({ thread, pickedId }): QuestionGroupExpertEntry => {
    const isPicked = pickedId !== undefined;

    const submitter = thread.attributedSubmitter ?? thread.author;

    const name = isPicked
      ? getThreadExpertName(thread)
      : getPersonName(submitter) || ANONYMOUS_SUBMITTER_LABEL;

    const avatar = (isPicked ? getThreadExpert(thread)?.avatar : submitter?.avatar) ?? null;

    const relayEntries = toRelayEntries(relaysByHostId.get(thread.conversationId) ?? []);

    const pickedEntry = relayEntries.find((entry) => isPicked && entry.expertId === pickedId);

    return {
      key: thread.conversationId,
      name,
      toAskAnonymously: !isPicked,
      expertId: pickedId ?? null,
      avatar,
      addedByStaff: false,
      hcpRow: {
        thread,
        name,
        lastName: getThreadLastName(thread),
        notSent: false,
        fromSubmitter: true,
        avatar,
      },
      expertRows: pickedEntry?.expertRows ?? [],
      // A closed pick is over rather than waiting to be sent, as in `findNotSentExperts`.
      expertNotSent:
        isPicked && !relayedExpertIds.has(pickedId) && getThreadStatus(thread) !== 'closed',
      children: relayEntries
        .filter((entry) => entry !== pickedEntry)
        .map((entry) => ({ ...entry, addedByStaff: isPicked })),
    };
  });

  const hasPicks = hcpThreads.some((hcp) => hcp.pickedId !== undefined);

  return [
    ...hcpEntries,
    ...toRelayEntries(unclaimedRelays).map((entry) => ({ ...entry, addedByStaff: hasPicks })),
  ];
}

/**
 * The submitter's threads with Ask Anonymously that hold an unread message once the question is
 * with experts, such as a follow-up the submitter wrote after the relays went out.
 *
 * They have no row, so the card header carries them: its dot and unread count, and a tap on the
 * header of an open card. Without that a follow-up would leave the card looking read. Before a
 * relay the submitter's threads are the rows already, so there is nothing to carry.
 */
export function getUnreadSubmitterThreads(
  questionGroup: Pick<QuestionGroup, 'threads' | 'notSentExperts' | 'originalGroup'>
): InquiryV2[] {
  if (!questionGroup.threads.length) {
    return [];
  }

  const rowIds = new Set(
    getQuestionGroupRows(questionGroup).map((row) => row.thread.conversationId)
  );

  return (questionGroup.originalGroup ?? []).filter(
    (thread) => thread.hasUnread && !rowIds.has(thread.conversationId)
  );
}

/**
 * What a tap on the inquiry group opens when it does not expand: the newest unread row's thread,
 * otherwise the newest by activity, with the conversations the chat shows as its sibling tabs. An
 * expert thread's tabs are the expert threads; a not-sent row is a fan-out thread, so its tabs are
 * the submitter's fan-out threads, as a flat tabbed group row opens them. A group with no row at all
 * opens the submitter's original the same way, so the target is never undefined.
 */
export function getQuestionGroupOpenTarget(
  questionGroup: Pick<QuestionGroup, 'original' | 'originalGroup' | 'threads' | 'notSentExperts'>
): { inquiry: InquiryV2; siblingConversationIds: string[] } {
  const fanOutIds = questionGroup.originalGroup.map((inquiry) => inquiry.conversationId);

  const rows = getQuestionGroupRows(questionGroup);

  const unread = [
    ...getUnreadSubmitterThreads(questionGroup).map(toSubmitterRow),
    ...rows.filter((row) => row.thread.hasUnread),
  ];

  const pool = unread.length ? unread : rows;

  const target = pool.reduce<QuestionGroupRow | undefined>(
    (newest, row) =>
      !newest ||
      getThreadActivityAt(row.thread).getTime() > getThreadActivityAt(newest.thread).getTime()
        ? row
        : newest,
    undefined
  );

  if (!target) {
    return { inquiry: questionGroup.original, siblingConversationIds: fanOutIds };
  }

  return {
    inquiry: target.thread,
    siblingConversationIds: target.fromSubmitter
      ? fanOutIds
      : questionGroup.threads.map((thread) => thread.conversationId),
  };
}

/** One chat tab for an expert thread of an inquiry group. */
export type QuestionGroupChatTab = {
  thread: InquiryV2;
  /** "Dr. Last" for an expert who identifies as a doctor, otherwise the full name. */
  label: string;
};

/**
 * The chat tabs for a conversation opened from an inquiry group: every expert thread the card
 * lists, in the order they were sent, when the conversation is one of them.
 *
 * Chat tabs otherwise come from one send, so experts relayed one at a time each opened alone
 * while the card held them together. Reading the card's own rows keeps the two in step.
 *
 * Undefined for the submitter's threads and the picks not yet sent, which keep the tabs of the
 * fan-out they belong to: in one strip the submitter's thread to an expert and the relay to the
 * same expert would carry the same name. Undefined too below two tabs, as there is nothing to
 * switch between.
 */
export function getQuestionGroupChatTabs(
  questionGroup: Pick<QuestionGroup, 'threads' | 'notSentExperts'> &
    Partial<Pick<QuestionGroup, 'originalGroup'>>,
  conversationId: string
): QuestionGroupChatTab[] | undefined {
  const expertRows = getQuestionGroupRows(questionGroup).filter((row) => !row.fromSubmitter);

  if (
    expertRows.length < 2 ||
    !expertRows.some((row) => row.thread.conversationId === conversationId)
  ) {
    return undefined;
  }

  return expertRows.map((row) => ({
    thread: row.thread,
    label: getGroupTabLabel({
      firstName: row.thread.recipient?.firstName,
      lastName: row.thread.recipient?.lastName,
      identifiesAsDoctor: row.thread.recipient?.identifiesAsDoctor,
    }),
  }));
}

export type QuestionGroupSummary = {
  unreadCount: number;
  /** Line 2 after "N unread": the age of the newest unread reply, or how long the longest wait is. */
  timeText: string;
  /** The words before the age, so the age alone can carry the exact-time tooltip. */
  timeLead: string;
  timeAgo: string;
  /** The date the time text counts from, for the exact-time tooltip. */
  timeAt: Date;
  /** "Expert: preview" for the thread whose reply a collapsed card shows, when it has text. */
  preview: string | null;
  /** The badge text for the exception states, or null when every thread is open. */
  closedLabel: string | null;
  closeRequestedLabel: string | null;
};

function describeCount(count: number, total: number, singular: string, plural: string) {
  if (count === 0) {
    return null;
  }

  return count === total ? singular : `${count} ${plural}`;
}

/**
 * `submitterSide`: the threads are the submitter's own to Ask Anonymously, where a close request is
 * staff marking the thread handled, so it is worded that way, as the chat says it.
 */
export function summarizeQuestionGroup(
  threads: readonly InquiryV2[],
  { submitterSide = false }: { submitterSide?: boolean } = {}
): QuestionGroupSummary {
  const unreadThreads = threads.filter((thread) => thread.hasUnread);

  const waitingThreads = threads.filter(isThreadWaiting);

  const repliedThreads = threads.filter((thread) => !isThreadWaiting(thread));

  const latestUnread = newestBy(unreadThreads, getThreadActivityAt);

  const longestWaiting = oldestBy(waitingThreads, getThreadSentAt);

  const newest = newestBy(threads, getThreadActivityAt);

  let timeLead = '';

  let timeAt = new Date(0);

  if (latestUnread) {
    timeAt = getThreadActivityAt(latestUnread);
  } else if (longestWaiting) {
    timeAt = getThreadSentAt(longestWaiting);

    timeLead = 'Sent ';
  } else if (newest) {
    timeAt = getThreadActivityAt(newest);
  }

  const timeAgo = formatAgo(timeAt);

  const previewThread = latestUnread ?? newestBy(repliedThreads, getThreadActivityAt);

  const previewText = previewThread?.lastMessagePreview?.trim();

  // A submitter-side thread's last message is the submitter's or staff's, never the picked
  // expert's, so it is not credited to anyone.
  const speaker = previewThread && !submitterSide ? `${getThreadExpertName(previewThread)}: ` : '';

  const preview = previewThread && previewText ? `${speaker}${previewText}` : null;

  const closedCount = threads.filter((thread) => getThreadStatus(thread) === 'closed').length;

  const closeRequestedCount = threads.filter(
    (thread) => getThreadStatus(thread) === 'close_requested'
  ).length;

  return {
    unreadCount: unreadThreads.length,
    timeText: `${timeLead}${timeAgo}`,
    timeLead,
    timeAgo,
    timeAt,
    preview,
    closedLabel: describeCount(closedCount, threads.length, 'Closed', 'closed'),
    closeRequestedLabel: describeCount(
      closeRequestedCount,
      threads.length,
      submitterSide ? 'Handled' : 'Close requested',
      submitterSide ? 'handled' : 'close requested'
    ),
  };
}

/**
 * Every thread a `both` card holds, each once: the HCP's threads and the relays. The header's
 * unread count and badge read all of them, since the card's entries show all of them, and a card
 * with no relays yet still has its HCP threads to carry an unread message.
 */
export function getBothSidesThreads(
  questionGroup: Pick<QuestionGroup, 'originalGroup' | 'threads'>
): InquiryV2[] {
  const byId = new Map<string, InquiryV2>();

  [...(questionGroup.originalGroup ?? []), ...questionGroup.threads].forEach((thread) => {
    if (!byId.has(thread.conversationId)) {
      byId.set(thread.conversationId, thread);
    }
  });

  return [...byId.values()];
}

/**
 * The summary of a `both` card. A close request on an HCP thread is staff marking it handled, as
 * the chat says it, while one on a relay is the expert asking to close, so each side is worded its
 * own way and the counts are combined for one badge. Both kinds on one card read as one count that
 * names both, rather than picking a side's wording for threads it does not describe.
 */
export function summarizeBothSidesQuestionGroup(
  questionGroup: Pick<QuestionGroup, 'originalGroup' | 'threads'>
): QuestionGroupSummary {
  const threads = getBothSidesThreads(questionGroup);

  const summary = summarizeQuestionGroup(threads);

  const relayIds = new Set(questionGroup.threads.map((thread) => thread.conversationId));

  const handledCount = threads.filter(
    (thread) =>
      !relayIds.has(thread.conversationId) && getThreadStatus(thread) === 'close_requested'
  ).length;

  const closeRequestedCount = threads.filter(
    (thread) => relayIds.has(thread.conversationId) && getThreadStatus(thread) === 'close_requested'
  ).length;

  const combinedCount = handledCount + closeRequestedCount;

  let closeRequestedLabel: string | null = null;

  if (closeRequestedCount === 0) {
    closeRequestedLabel = describeCount(handledCount, threads.length, 'Handled', 'handled');
  } else if (handledCount === 0) {
    closeRequestedLabel = describeCount(
      closeRequestedCount,
      threads.length,
      'Close requested',
      'close requested'
    );
  } else {
    closeRequestedLabel =
      combinedCount === threads.length
        ? 'Handled or close requested'
        : `${combinedCount} handled or close requested`;
  }

  return { ...summary, closeRequestedLabel };
}

/**
 * The experts a `both` card's entries show, each once: the one an HCP thread picked, the ones
 * relayed to under a thread, and one no HCP thread claims. An HCP thread that picked nobody is the
 * HCP, not an expert, so it counts for none. The header's count and names line read this, so they
 * agree with the entries.
 */
export function getBothSidesExperts(
  entries: readonly QuestionGroupExpertEntry[]
): { key: string; lastName: string }[] {
  const experts = new Map<string, string>();

  const add = (key: string, lastName: string) => {
    if (!experts.has(key)) {
      experts.set(key, lastName);
    }
  };

  const addRelayEntry = (entry: QuestionGroupExpertEntry) =>
    add(
      entry.expertId === null ? entry.key : `expert-${entry.expertId}`,
      entry.expertRows[0]?.lastName ?? entry.name
    );

  entries.forEach((entry) => {
    if (entry.hcpRow && !entry.toAskAnonymously) {
      add(`expert-${entry.expertId}`, entry.hcpRow.lastName);
    }

    if (!entry.hcpRow) {
      addRelayEntry(entry);
    }

    entry.children.forEach(addRelayEntry);
  });

  return [...experts].map(([key, lastName]) => ({ key, lastName }));
}

/** Why an expert is on a `both` card, said beside their name when it is not a plain relay. */
export type QuestionGroupExpertNote = 'asked for' | 'added by staff' | null;

/**
 * One line of an expanded `both` card. The card reads the same for every shape: the HCP's line
 * once, then one line per expert, all at one level.
 */
export type QuestionGroupLine =
  | {
      kind: 'hcp';
      key: string;
      /** The HCP inquiry the line opens: an unread one first, else the one with the newest message. */
      row: QuestionGroupRow;
      name: string;
      avatar: QuestionGroupRow['avatar'];
      /** How many HCP inquiries the line stands for. The chat's tabs reach the others. */
      inquiryCount: number;
    }
  | {
      kind: 'expert';
      key: string;
      name: string;
      avatar: QuestionGroupRow['avatar'];
      /** Ask Anonymously's relays to this expert, newest first. Empty while a pick waits to be sent. */
      rows: QuestionGroupRow[];
      note: QuestionGroupExpertNote;
      notSent: boolean;
    };

const NOTE_RANK: Record<NonNullable<QuestionGroupExpertNote>, number> = {
  'asked for': 2,
  'added by staff': 1,
};

function strongerNote(left: QuestionGroupExpertNote, right: QuestionGroupExpertNote) {
  return (left ? NOTE_RANK[left] : 0) >= (right ? NOTE_RANK[right] : 0) ? left : right;
}

/** The HCP inquiry a card's HCP line opens: an unread one first, else the one whose message is newest. */
function pickHcpRow(rows: QuestionGroupRow[]) {
  const latest = (row: QuestionGroupRow) => new Date(row.thread.lastResponseDate ?? 0).getTime();

  return [...rows].sort(
    (left, right) =>
      Number(Boolean(right.thread.hasUnread)) - Number(Boolean(left.thread.hasUnread)) ||
      latest(right) - latest(left)
  )[0];
}

/**
 * Flattens a `both` card's entries into one level: the HCP once, then each expert once. Every shape
 * a question takes, one inquiry or several, picked or not, relays no inquiry claims, ends up as the
 * same list, and what made an expert different becomes a note on their line. An expert reached
 * from several of the HCP's inquiries is one line holding every relay. Which inquiry an expert came
 * from is left to the chat's tabs.
 */
export function getQuestionGroupLines(
  entries: readonly QuestionGroupExpertEntry[],
  submitter: InquiryV2['author'] | null | undefined
): QuestionGroupLine[] {
  const hcpRows = entries.flatMap((entry) => (entry.hcpRow ? [entry.hcpRow] : []));

  const experts = new Map<string, Extract<QuestionGroupLine, { kind: 'expert' }>>();

  const addExpert = (
    entry: QuestionGroupExpertEntry,
    rows: QuestionGroupRow[],
    note: QuestionGroupExpertNote,
    notSent: boolean
  ) => {
    const key = entry.expertId === null ? entry.key : `expert-${entry.expertId}`;

    const existing = experts.get(key);

    if (existing) {
      existing.rows.push(...rows);

      existing.note = strongerNote(existing.note, note);

      existing.notSent = existing.notSent && notSent;

      return;
    }

    experts.set(key, {
      kind: 'expert',
      key,
      name: entry.name,
      avatar: entry.avatar,
      rows: [...rows],
      note,
      notSent,
    });
  };

  entries.forEach((entry) => {
    const picked = Boolean(entry.hcpRow) && !entry.toAskAnonymously;

    if (picked) {
      addExpert(
        entry,
        entry.expertRows,
        'asked for',
        entry.expertNotSent && !entry.expertRows.length
      );
    } else if (!entry.hcpRow && entry.expertRows.length) {
      addExpert(entry, entry.expertRows, entry.addedByStaff ? 'added by staff' : null, false);
    }

    entry.children.forEach((child) =>
      addExpert(child, child.expertRows, child.addedByStaff ? 'added by staff' : null, false)
    );
  });

  // "Asked for" only tells experts apart when some were not asked for. On a card where the HCP
  // picked every expert, it would repeat on every line and say nothing.
  const everyExpertAskedFor = [...experts.values()].every((line) => line.note === 'asked for');

  const expertLines = [...experts.values()].map((line) => ({
    ...line,
    note: everyExpertAskedFor ? null : line.note,
    rows: [...line.rows].sort(
      (left, right) =>
        new Date(right.thread.lastResponseDate ?? 0).getTime() -
        new Date(left.thread.lastResponseDate ?? 0).getTime()
    ),
    notSent: line.notSent && line.rows.length === 0,
  }));

  if (!hcpRows.length) {
    return expertLines;
  }

  const opened = pickHcpRow(hcpRows);

  const name = getPersonName(submitter) || ANONYMOUS_SUBMITTER_LABEL;

  const avatar = submitter?.avatar ?? null;

  return [
    {
      kind: 'hcp',
      key: hcpRows[0].thread.conversationId,
      row: { ...opened, name, avatar },
      name,
      avatar,
      inquiryCount: hcpRows.length,
    },
    ...expertLines,
  ];
}

/** The expert's last name, which is how the inquiry group lists who holds it. */
export function getThreadLastName(thread: InquiryV2) {
  return getThreadExpert(thread)?.lastName?.trim() || getThreadExpertName(thread);
}

/**
 * The experts an inquiry group was sent to, as last names: at most `max`, then "+N". The same name is not
 * listed twice, so two experts who share a surname show once.
 */
export function formatQuestionGroupNames(
  threads: readonly InquiryV2[],
  notSentExperts: readonly Pick<NotSentExpert, 'lastName'>[] = [],
  max = 3
) {
  return formatLastNames(
    [...threads.map(getThreadLastName), ...notSentExperts.map((expert) => expert.lastName)],
    max
  );
}

/** Last names as one line: at most `max`, then "+N", each name once. */
export function formatLastNames(lastNames: readonly string[], max = 3) {
  const names = Array.from(new Set(lastNames));

  const shown = names.slice(0, max);

  const extra = names.length - shown.length;

  return extra > 0 ? `${shown.join(', ')} +${extra}` : shown.join(', ');
}

/** A thread's latest message, only once the expert has written back. */
export function getThreadPreview(thread: InquiryV2) {
  if (isThreadWaiting(thread)) {
    return null;
  }

  return thread.lastMessagePreview?.trim() || null;
}

/**
 * The line under a row's name. An expert thread shows its latest reply once there is one. A picked
 * expert's row is the submitter's own thread with Ask Anonymously, so it shows the submitter's
 * latest message, attributed to the submitter so it never reads as the expert's. The submitter's
 * own row is already named for them, so its message needs no attribution.
 */
export function getRowPreview(row: Pick<QuestionGroupRow, 'thread' | 'notSent' | 'fromSubmitter'>) {
  if (!row.fromSubmitter) {
    return getThreadPreview(row.thread);
  }

  const message = row.thread.lastMessagePreview?.trim();

  if (!message) {
    return null;
  }

  // A pick's thread that holds only the question would repeat it on every pick's row, so the row
  // says where it stands instead. A follow-up the submitter added to this pick still shows.
  if (row.thread.lastMessageIndex === 0) {
    return null;
  }

  if (!row.notSent) {
    return message;
  }

  const submitterName = getPersonName(row.thread.attributedSubmitter ?? row.thread.author);

  return submitterName ? `${submitterName}: ${message}` : message;
}

export type QuestionGroupBadge = { tone: 'unread' | 'muted'; label: string };

/**
 * The one badge on a collapsed inquiry group, the most important state first: something to read, then a
 * close request, then a closed thread, then an expert not sent to yet. An inquiry group where everyone is simply waiting has none.
 */
export function getQuestionGroupBadge(
  summary: Pick<QuestionGroupSummary, 'unreadCount' | 'closeRequestedLabel' | 'closedLabel'>
): QuestionGroupBadge | null {
  if (summary.unreadCount > 0) {
    return { tone: 'unread', label: `${summary.unreadCount} unread` };
  }

  if (summary.closeRequestedLabel) {
    return { tone: 'muted', label: summary.closeRequestedLabel };
  }

  if (summary.closedLabel) {
    return { tone: 'muted', label: summary.closedLabel };
  }

  return null;
}

/** A thread's second line: "Sent 1 day ago" while it waits, otherwise "preview, 3 hours ago". */
export function getThreadSecondLine(thread: InquiryV2) {
  if (isThreadWaiting(thread)) {
    const at = getThreadSentAt(thread);

    const ago = formatAgo(at);

    return { lead: 'Sent ', ago, at, text: `Sent ${ago}` };
  }

  const at = getThreadActivityAt(thread);

  const ago = formatAgo(at);

  const preview = thread.lastMessagePreview?.trim();

  const lead = preview ? `${preview}${TITLE_SEPARATOR}` : '';

  return { lead, ago, at, text: `${lead}${ago}` };
}

/**
 * The inquiry group title: cancer type, the submitter, then a short case line. It never carries an expert
 * name. The submitter's name belongs to a staff surface only, so callers gate the whole card on
 * staff before this runs. The card draws the case line on a line of its own under the heading and
 * uses the whole title as its spoken name.
 *
 * The list row carries `attributedSubmitter` only when support wrote on someone's behalf, so the
 * original's author stands in otherwise. With no usable name the title says so rather than
 * guessing. The case line is the written clinical case line, falling back to the diagnosis text,
 * and is left out when neither exists.
 */
export function buildQuestionGroupTitle(
  original: InquiryV2,
  originalGroup: readonly InquiryV2[] = [original]
) {
  return [buildQuestionGroupHeading(original), getQuestionGroupCaseLine(original, originalGroup)]
    .filter(Boolean)
    .join(TITLE_SEPARATOR);
}

/** The title's first line: cancer type and the submitter, without the case line. */
export function buildQuestionGroupHeading(original: InquiryV2) {
  const cancerLabel = original.cancerType?.shortLabel ?? original.cancerType?.label ?? '';

  const person = original.attributedSubmitter ?? original.author;

  const personName = person
    ? getProfileNameAndInitials({
        firstName: person.firstName,
        lastName: person.lastName,
        identifiesAsDoctor: person.identifiesAsDoctor,
      }).name
    : '';

  const submitter =
    personName && !isAskAnonymously(personName) ? personName : ANONYMOUS_SUBMITTER_LABEL;

  return [cancerLabel, submitter].filter(Boolean).join(TITLE_SEPARATOR);
}

/**
 * The case line that ends an inquiry group's title: the written clinical case, else the question
 * itself while one of the submitter's threads still holds only it. It is the part of the title that
 * runs long, so a hover shows it alone in full.
 *
 * The question stands in until a case is written so a card does not sit empty under its heading.
 * There is no field for the question's text, but a thread's last message is the question for as
 * long as it is the thread's only message. The form no longer asks for a diagnosis, so that field
 * is not used: it showed only what older inquiries or a seeding script typed there, such as "R/R".
 */
export function getQuestionGroupCaseLine(
  original: InquiryV2,
  originalGroup: readonly InquiryV2[] = [original]
) {
  if (original.clinicalCase?.status === 'written') {
    return original.clinicalCase.line.trim();
  }

  const questionThread = [original, ...originalGroup].find(
    (thread) => thread.lastMessageIndex === 0 && thread.lastMessagePreview?.trim()
  );

  return questionThread?.lastMessagePreview?.trim() ?? '';
}
